"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import * as Sentry from "@sentry/nextjs";

interface MermaidDiagramProps {
  chart: string;
  id?: string;
}

// React 요소에서 텍스트를 추출하는 함수
function extractTextFromNode(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromNode).join("");
  if (
    node &&
    typeof node === "object" &&
    node !== null &&
    "props" in node &&
    node.props &&
    typeof node.props === "object" &&
    node.props !== null &&
    "children" in node.props
  ) {
    return extractTextFromNode(node.props.children as React.ReactNode);
  }
  return "";
}

export default function MermaidDiagram({ chart, id }: MermaidDiagramProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [svgContent, setSvgContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const rawChartText =
    typeof chart === "string" ? chart : extractTextFromNode(chart);

  // mermaid 비동기 렌더링 — 외부 라이브러리 호출 + chart 변경 시 상태 리셋
  useEffect(() => {
    let mermaidLoaded = false;

    // 상태 초기화
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);
    setError(null);
    setSvgContent("");

    const loadMermaid = async () => {
      if (mermaidLoaded) return;

      try {
        // chart가 문자열이 아닌 경우 텍스트 추출
        let chartText: string;
        if (typeof chart === "string") {
          chartText = chart;
        } else {
          chartText = extractTextFromNode(chart);
        }

        // mermaid YAML front-matter를 위한 들여쓰기 정리
        // YAML front-matter의 --- 는 줄의 맨 앞에서 시작해야 함
        const lines = chartText.split("\n");
        const cleanedLines = lines.map((line) => {
          // --- 로 시작하는 줄은 들여쓰기 제거
          if (line.trim() === "---") {
            return "---";
          }
          // YAML config 블록의 들여쓰기는 유지하되, 전체적인 공통 들여쓰기만 제거
          return line;
        });

        // 공통 들여쓰기 제거 (모든 줄이 동일한 들여쓰기를 가지고 있다면)
        const nonEmptyLines = cleanedLines.filter((line) => line.trim() !== "");
        if (nonEmptyLines.length > 0) {
          const minIndent = Math.min(
            ...nonEmptyLines.map((line) => {
              const match = line.match(/^(\s*)/);
              return match ? match[1].length : 0;
            })
          );

          if (minIndent > 0) {
            for (let i = 0; i < cleanedLines.length; i++) {
              if (cleanedLines[i].trim() !== "") {
                cleanedLines[i] = cleanedLines[i].substring(minIndent);
              }
            }
          }
        }

        chartText = cleanedLines.join("\n");

        // 빈 문자열이나 유효하지 않은 chart인 경우 처리
        if (
          !chartText ||
          typeof chartText !== "string" ||
          chartText.trim() === ""
        ) {
          setError("차트 데이터가 유효하지 않습니다.");
          setIsLoading(false);
          return;
        }

        // 동적으로 mermaid를 import
        const mermaid = (await import("mermaid")).default;

        // 테마 설정 (초기 렌더링 시 테마가 undefined일 수 있으므로 시스템 테마 확인)
        const getEffectiveTheme = () => {
          if (theme === "dark") return true;
          if (theme === "light") return false;
          // theme이 undefined이거나 system인 경우 시스템 테마 확인
          if (typeof window !== "undefined") {
            return window.matchMedia("(prefers-color-scheme: dark)").matches;
          }
          return false; // 서버사이드에서는 라이트 모드 기본
        };

        const isDark = getEffectiveTheme();

        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? "dark" : "default",
          themeVariables: {
            // 기본 색상
            primaryColor: isDark ? "#60a5fa" : "#3b82f6",
            primaryTextColor: isDark ? "#ffffff" : "#1f2937",
            primaryBorderColor: isDark ? "#374151" : "#374151",
            lineColor: isDark ? "#6b7280" : "#374151",

            // 노드 관련 색상
            nodeBkg: isDark ? "#374151" : "#f8fafc",
            nodeTextColor: isDark ? "#ffffff" : "#1f2937",
            nodeBorder: isDark ? "#6b7280" : "#374151",

            // 엣지/화살표 색상
            edgeLabelBackground: isDark ? "#1f2937" : "#ffffff",
            arrowheadColor: isDark ? "#6b7280" : "#374151",

            // 섹션 배경
            sectionBkgColor: isDark ? "#1f2937" : "#f9fafb",
            altSectionBkgColor: isDark ? "#374151" : "#f3f4f6",

            // 그리드 색상
            gridColor: isDark ? "#4b5563" : "#e5e7eb",

            // 클러스터 색상
            clusterBkg: isDark ? "#1f2937" : "#f8fafc",
            clusterBorder: isDark ? "#6b7280" : "#374151",

            // 일반 색상 팔레트
            c0: isDark ? "#1f2937" : "#ffffff",
            c1: isDark ? "#374151" : "#f8fafc",
            c2: isDark ? "#4b5563" : "#f1f5f9",
            c3: isDark ? "#6b7280" : "#e2e8f0",
            c4: isDark ? "#9ca3af" : "#cbd5e1",

            // 텍스트 색상
            textColor: isDark ? "#ffffff" : "#1f2937",
            mainBkg: isDark ? "#1f2937" : "#ffffff",

            // 라벨 색상
            labelTextColor: isDark ? "#ffffff" : "#1f2937",
            labelBoxBkgColor: isDark ? "#374151" : "#f8fafc",
            labelBoxBorderColor: isDark ? "#6b7280" : "#374151",
          },
        });

        const uniqueId =
          id || `mermaid-${Math.random().toString(36).substr(2, 9)}`;

        try {
          const { svg } = await mermaid.render(uniqueId, chartText);
          setSvgContent(svg);
          setError(null);
          setIsLoading(false);
        } catch (err) {
          console.error("Mermaid rendering error:", err);
          Sentry.captureException(err, {
            tags: { component: 'MermaidDiagram', operation: 'render' },
            extra: { chartLength: chartText.length }
          });
          setError(`렌더링 오류: ${err}`);
          setSvgContent("");
          setIsLoading(false);
        }

        mermaidLoaded = true;
      } catch (err) {
        console.error("Failed to load mermaid:", err);
        Sentry.captureException(err, {
          tags: { component: 'MermaidDiagram', operation: 'loadMermaid' }
        });
        setError(
          "Mermaid 라이브러리를 로드할 수 없습니다. yarn add mermaid 명령어로 mermaid 패키지를 설치해주세요."
        );
        setSvgContent("");
        setIsLoading(false);
      }
    };

    // 약간의 지연을 두어 다른 컴포넌트들이 렌더링된 후 mermaid 로드
    const timeoutId = setTimeout(() => {
      loadMermaid();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [chart, theme, id]);

  return (
    <div
      className="my-6"
      data-llm-mermaid-block="true"
      data-llm-code-text={rawChartText}
    >
      <div
        ref={elementRef}
        className="flex justify-center items-center min-h-[200px] bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
      >
        {isLoading && (
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              다이어그램을 로드하는 중...
            </p>
          </div>
        )}

        {!isLoading && error && (
          <div className="p-4 border border-red-300 bg-red-50 rounded-lg dark:bg-red-900 dark:border-red-700 w-full">
            <p className="text-red-700 dark:text-red-300 font-semibold">
              Mermaid 다이어그램 오류
            </p>
            <p className="text-sm mt-2 text-red-600 dark:text-red-400">
              {error}
            </p>
          </div>
        )}

        {!isLoading && !error && svgContent && (
          <div
            className="w-full flex justify-center"
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        )}
      </div>
    </div>
  );
}
